/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  Avatar,
  Colors,
  Img,
  Typography,
  VisuallyHidden,
} from '@zapier/design-system';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../constants';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';

export interface TweetProps {
  ariaHidden?: boolean;
  avatarUrl?: string;
  body: string;
  handle: string;
  mediaUrl?: string;
  mediaAltText?: string;
  name: string;
  url?: string;
}

const Styles = labelStyles('Tweet', {
  root: (hasLink: boolean) =>
    css({
      width: '320px',
      padding: '16px',
      marginBottom: '20px',
      position: 'relative',
      boxSizing: 'border-box',
      breakInside: 'avoid-column',
      backgroundColor: Colors.white,
      border: `1px solid ${Colors.neutral800}`,
      borderRadius: '5px',
      transition: 'box-shadow 250ms',

      [MEDIA_MIN_WIDTH.large]: {
        width: '512px',
        padding: '24px',
      },

      ...(hasLink && {
        '&:hover, &:focus-within': {
          boxShadow: `6px 6px 0 ${Colors.neutral800}`,
        },
      }),
    }),

  header: css({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',

    [MEDIA_MIN_WIDTH.large]: {
      marginBottom: '16px',
    },

    '& > :last-child': {
      marginLeft: '8px',

      [MEDIA_MIN_WIDTH.large]: {
        marginLeft: '12px',
      },
    },
  }),

  name: css({
    ...Typography.smallPrint1Medium,
    lineHeight: 1.25,
    color: Colors.neutral800,
  }),

  handle: css({
    ...Typography.smallPrint1,
    lineHeight: 1.25,
    color: Colors.neutral700,
  }),

  body: css({
    ...Typography.smallPrint1,

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1Short,
    },
  }),

  media: css({
    marginTop: '12px',

    [MEDIA_MIN_WIDTH.large]: {
      marginTop: '16px',
    },
  }),

  video: css({
    width: '100%',
    borderRadius: '10px',
  }),

  link: css({
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    outline: 'none',
  }),
});

export function Tweet(props: TweetProps) {
  return (
    <div aria-hidden={props.ariaHidden} css={Styles.root(!!props.url)}>
      <header css={Styles.header}>
        <Avatar name={props.name} size={40} url={props.avatarUrl} />
        <div>
          <div css={Styles.name}>{props.name}</div>
          <div css={Styles.handle}>{props.handle}</div>
        </div>
      </header>
      <div css={Styles.body}>
        <RichTextHtml backgroundColor={COLOR_KEYS.white} html={props.body} />
      </div>
      {props.mediaUrl && (
        <div css={Styles.media}>
          {props.mediaUrl.toLowerCase().endsWith('.mp4') ||
          props.mediaUrl.toLowerCase().endsWith('.mov') ? (
            <video
              aria-label={props.mediaAltText}
              autoPlay={true}
              controls={false}
              css={Styles.video}
              disablePictureInPicture={true}
              loop={true}
              muted={true}
              playsInline={true}
              preload="auto"
              src={props.mediaUrl}
            />
          ) : (
            <Img
              alt={props.mediaAltText || ''}
              isFullWidth={true}
              src={props.mediaUrl}
            />
          )}
        </div>
      )}
      {props.url && (
        <a css={Styles.link} href={props.url} target="_blank" rel="noreferrer">
          <VisuallyHidden>View tweet</VisuallyHidden>
        </a>
      )}
    </div>
  );
}
